<template>
  <!-- One app's Manage page: where it lives, how to sign in, what to do next,
       plus app-specific tools (e.g. approving OpenClaw browsers). Everything
       comes from the server itself (the app's folder, read via the guest agent). -->
  <div>
    <div class="d-flex align-center mb-2">
      <gg-btn small text class="kvm-dk-text ml-n2" @click="$emit('back')">
        <v-icon left small aria-hidden="true">mdi-arrow-left</v-icon>Back to apps
      </gg-btn>
    </div>

    <div v-if="loading && !detail" class="py-8 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading {{ name }}…</span>
    </div>
    <v-alert v-else-if="error && !detail" type="error" border="left" dense role="alert">{{ error }}</v-alert>

    <template v-else-if="detail">
      <!-- Header -->
      <div class="d-flex flex-wrap align-center mb-4">
        <kvm-logo :item="app || { slug: name }" :size="40" default-icon="mdi-docker" class="mr-3" />
        <div class="mr-auto">
          <h3 class="body-1 font-weight-medium mb-0">{{ app && app.name ? app.name : name }}</h3>
          <div class="kvm-help text-caption">
            <template v-if="app && app.name">App “{{ name }}” · </template>
            <span v-if="busyLabel" class="kvm-dk-busy">
              <v-progress-circular indeterminate size="12" width="2" color="primary" class="mr-1" aria-hidden="true" />{{ busyLabel }}
            </span>
            <template v-else>{{ statusText }}</template>
          </div>
        </div>
        <div class="text-no-wrap mt-1">
          <gg-btn v-if="openUrl" small tile color="primary" class="kvm-dk-primary mr-2" :href="openUrl" target="_blank" rel="noopener">
            <v-icon left small aria-hidden="true">mdi-open-in-new</v-icon>Open
          </gg-btn>
          <gg-btn small tile outlined color="primary" class="mr-1" :disabled="!!busyLabel" @click="$emit('action', 'restart')">Restart</gg-btn>
          <gg-btn v-if="running" small tile outlined color="primary" class="mr-1" :disabled="!!busyLabel" @click="$emit('action', 'stop')">Stop</gg-btn>
          <gg-btn v-else small tile outlined color="primary" class="mr-1" :disabled="!!busyLabel" @click="$emit('action', 'start')">Start</gg-btn>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <gg-btn icon small aria-label="More actions" :disabled="!!busyLabel" v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></gg-btn>
            </template>
            <v-list dense>
              <v-list-item v-if="managed" @click="$emit('action', 'up')"><v-list-item-title>Redeploy</v-list-item-title></v-list-item>
              <v-list-item v-if="managed" @click="$emit('action', 'update')"><v-list-item-title>Update to the newest build</v-list-item-title></v-list-item>
              <v-list-item @click="$emit('edit')"><v-list-item-title>{{ managed ? 'Edit compose file' : 'View details' }}</v-list-item-title></v-list-item>
              <v-list-item @click="$emit('action', 'down')"><v-list-item-title>Take down (remove containers)</v-list-item-title></v-list-item>
              <v-list-item v-if="managed" @click="$emit('remove')"><v-list-item-title class="error--text">Delete app…</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <!-- Browser approval (OpenClaw) -->
      <section v-if="pairing" class="kvm-dk-section" aria-labelledby="kvm-dk-m-pair">
        <h4 id="kvm-dk-m-pair" class="subtitle-2 mb-1">Browser access</h4>
        <p class="mb-2">
          Each new browser you sign in with has to be approved once. Open the app, sign in, and when it asks for approval, approve it here.
        </p>
        <gg-btn small tile color="primary" class="kvm-dk-primary" @click="pairingOpen = true">
          <v-icon left small aria-hidden="true">mdi-check-decagram</v-icon>Approve a browser
        </gg-btn>
      </section>

      <!-- Where it lives -->
      <section class="kvm-dk-section" aria-labelledby="kvm-dk-m-where">
        <h4 id="kvm-dk-m-where" class="subtitle-2 mb-1">Address</h4>
        <ul v-if="detail.routes.length || (app && app.url)" class="kvm-dk-list">
          <li v-for="r in detail.routes" :key="r.hostname + r.path">
            <a :href="routeUrl(r)" target="_blank" rel="noopener" class="kvm-dk-link">{{ routeUrl(r) }}</a>
          </li>
          <li v-if="!detail.routes.length && app && app.url">
            <a :href="app.url" target="_blank" rel="noopener" class="kvm-dk-link">{{ app.url }}</a>
          </li>
        </ul>
        <p v-else class="kvm-help mb-0">Not published at a domain. Add one in the Domains tab.</p>
        <p v-if="detail.published_ports.length" class="kvm-help text-caption mb-0 mt-1">
          Ports on this server: {{ detail.published_ports.join(', ') }}
        </p>
      </section>

      <!-- Version & updates -->
      <section class="kvm-dk-section" aria-labelledby="kvm-dk-m-ver">
        <div class="d-flex flex-wrap align-center mb-1">
          <h4 id="kvm-dk-m-ver" class="subtitle-2 mb-0 mr-auto">Version</h4>
          <gg-btn v-if="updates.length" small tile color="primary" class="kvm-dk-primary mr-2" :disabled="!!busyLabel" @click="$emit('action', 'update')">
            <v-icon left small aria-hidden="true">mdi-update</v-icon>Update now
          </gg-btn>
          <gg-btn small tile outlined color="primary" :loading="checking" :disabled="!!busyLabel || !versions.length" @click="checkUpdates">
            <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Check for updates
          </gg-btn>
        </div>
        <p v-if="versionsError" class="kvm-help mb-0">{{ versionsError }}</p>
        <div v-for="v in versions" :key="v.image" class="kvm-dk-ver">
          <div>
            <span class="font-weight-medium">{{ v.services.join(', ') }}</span>
            <span class="kvm-help text-caption kvm-dk-mono ml-1">{{ v.image }}</span>
          </div>
          <div class="text-caption">
            Installed: <strong>{{ buildText(v.installed) }}</strong>
            <template v-if="checked">
              ·
              <span v-if="v.error" class="kvm-help">{{ v.error }}</span>
              <span v-else-if="v.update_available" class="kvm-dk-new">Update available: <strong>{{ buildText(v.latest) }}</strong></span>
              <span v-else-if="v.update_available === false" class="kvm-dk-ok">
                <v-icon x-small color="success" aria-hidden="true">mdi-check-circle</v-icon> Up to date
              </span>
            </template>
          </div>
        </div>
        <p v-if="versions.length" class="kvm-help text-caption mb-0 mt-1">
          Updates install the newest build of each image's tag ({{ tagsText }}) and restart the app; your data is kept.
          Moving to a new major version means changing the tag in the compose file.
        </p>
      </section>

      <!-- Sign-in details -->
      <section v-if="app && app.credentials.length" class="kvm-dk-section" aria-labelledby="kvm-dk-m-creds">
        <h4 id="kvm-dk-m-creds" class="subtitle-2 mb-1">Sign-in details</h4>
        <div v-for="c in app.credentials" :key="c.label" class="d-flex align-center mb-1">
          <span class="kvm-help text-caption mr-2" style="min-width: 140px;">{{ c.label }}</span>
          <kvm-copyable :value="c.value" :display="reveal[c.label] ? c.value : '••••••••••••'" />
          <gg-btn icon x-small class="ml-1" :aria-label="(reveal[c.label] ? 'Hide ' : 'Show ') + c.label" @click="$set(reveal, c.label, !reveal[c.label])">
            <v-icon x-small>{{ reveal[c.label] ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
          </gg-btn>
        </div>
        <p class="kvm-help text-caption mb-0 mt-1">Keep these private. They're stored only on your server, with the app.</p>
      </section>

      <!-- Next steps from the template -->
      <section v-if="notesHtml" class="kvm-dk-section" aria-labelledby="kvm-dk-m-notes">
        <h4 id="kvm-dk-m-notes" class="subtitle-2 mb-1">Getting started</h4>
        <div class="kvm-dk-md" v-html="notesHtml" />
      </section>

      <p v-if="!app" class="kvm-help text-caption">
        {{ managed ? 'This app was installed from a compose file (or before app details were saved), so there are no setup notes for it.' : 'This app was created outside this panel.' }}
      </p>

      <!-- What it's made of -->
      <section class="kvm-dk-section" aria-labelledby="kvm-dk-m-parts">
        <h4 id="kvm-dk-m-parts" class="subtitle-2 mb-1">Containers</h4>
        <v-simple-table v-if="detail.containers.length" dense class="kvm-dk-table">
          <caption class="kvm-sr-only">Containers of {{ name }}</caption>
          <tbody>
            <tr v-for="c in detail.containers" :key="c.name">
              <td class="font-weight-medium">{{ c.service }}</td>
              <td class="kvm-dk-mono">{{ c.image }}</td>
              <td>{{ c.status }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <p v-else class="kvm-help mb-0">No containers right now. Use Redeploy to start it.</p>
        <p v-if="detail.volumes.length" class="kvm-help text-caption mb-0 mt-2">
          Data volumes: <span class="kvm-dk-mono">{{ detail.volumes.join(', ') }}</span>
        </p>
      </section>
    </template>

    <kvm-docker-pairing v-if="pairing" v-model="pairingOpen" :service-id="serviceId" :stack="name"
                        :app-name="app && app.name ? app.name : name" />
  </div>
</template>

<script>
import kvm from '@/api/kvm'
import { renderMarkdown } from '@/helpers/markdown'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'
import KvmDockerPairing from '@/views/services/hosting/kvm/KvmDockerPairing'
import KvmLogo from '@/views/services/hosting/kvm/KvmLogo'

export default {
  name: 'KvmDockerAppManage',
  components: { KvmCopyable, KvmDockerPairing, KvmLogo },
  props: {
    serviceId: { type: [String, Number], required: true },
    // The row from the Apps list: { name, status, managed, device_pairing }.
    stack: { type: Object, required: true },
    // Set while an action on this app runs ("Restarting…").
    busyLabel: { type: String, default: '' },
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    detail: null,
    loading: false,
    error: '',
    reveal: {},
    pairingOpen: false,
    versions: [],
    versionsError: '',
    checking: false,
    checked: false,
  }),
  computed: {
    name () { return this.stack.name },
    managed () { return !!this.stack.managed },
    app () { return this.detail && this.detail.app },
    pairing () { return !!(this.stack.device_pairing || (this.detail && this.detail.device_pairing) || (this.app && this.app.device_pairing)) },
    running () {
      return this.detail ? this.detail.containers.some(c => c.state === 'running') : /running/.test(this.stack.status || '')
    },
    statusText () {
      if (!this.detail) return this.stack.status
      const cs = this.detail.containers
      if (!cs.length) return 'Not running'
      const up = cs.filter(c => c.state === 'running').length
      return up === cs.length ? 'Running' : `${up} of ${cs.length} containers running`
    },
    openUrl () {
      if (this.detail && this.detail.routes.length) return this.routeUrl(this.detail.routes[0])
      return (this.app && this.app.url) || ''
    },
    notesHtml () { return renderMarkdown(this.app && this.app.notes) },
    updates () { return this.versions.filter(v => v.update_available) },
    tagsText () { return [...new Set(this.versions.map(v => `“${v.tag}”`))].join(', ') },
  },
  watch: {
    // An operation finished (e.g. the update) → fresh details and versions.
    refreshKey () { this.load() },
    'stack.name' () { this.detail = null; this.load() },
  },
  methods: {
    async load () {
      this.loading = true
      try {
        this.detail = await kvm.dockerStack(this.serviceId, this.name)
        this.error = ''
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
      this.loadVersions(false)
    },
    async loadVersions (check) {
      try {
        this.versions = await kvm.dockerVersions(this.serviceId, this.name, check)
        this.checked = check
        this.versionsError = ''
      } catch (e) {
        this.versions = []
        this.versionsError = e.status === 404 ? 'Not running — redeploy it to see its version.' : e.message
      }
    },
    async checkUpdates () {
      this.checking = true
      try {
        await this.loadVersions(true)
      } finally {
        this.checking = false
      }
    },
    // "2.0.4 (built 16 Sep 2026)", or just the build date when the image has no version.
    buildText (b) {
      if (!b) return 'unknown'
      const date = b.created ? new Date(b.created).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
      if (b.version) return date ? `${b.version} (built ${date})` : b.version
      return date ? `build of ${date}` : (b.digest ? b.digest.slice(7, 19) : 'unknown')
    },
    routeUrl (r) {
      return r ? 'https://' + r.hostname + (r.path && r.path !== '/' ? r.path + '/' : '/') : ''
    },
  },
  mounted () {
    this.load()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-dk-mono { font-family: monospace; font-size: 0.8rem; }
.kvm-dk-primary { @include kvm-btn-primary-aa; }
.kvm-dk-text { @include kvm-text-green-aa; }
.kvm-dk-link { @include kvm-text-green-aa; }
.kvm-dk-busy { display: inline-flex; align-items: center; font-style: italic; }
.kvm-dk-list { padding-left: 1.2rem; margin-bottom: 0; }
.kvm-dk-ver { padding: 4px 0; }
.kvm-dk-new { color: #b45309; }
.kvm-dk-ok { white-space: nowrap; }
.kvm-dk-section {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 12px 0;
}
.kvm-dk-md ::v-deep p:last-child { margin-bottom: 0; }
.kvm-dk-table { @include kvm-stacked-table; }
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
